import React from "react";
import { PropTypes } from "prop-types";
import Autocomplete, {
  autocompleteDropdownComponentsProps,
  VisuallyHiddenCopyText,
} from "./style/ERPSBaseAutoCompleteStyles";
import { withERPSTextFieldActionValidator } from "../../hoc/withERPSTextFieldActionValidator";
import { FormControl, TextField } from "../../primitives";

/**
 * ERPSBaseAutoComplete
 *
 * An editable combobox built on top of MUI Autocomplete. It behaves like a
 * dropdown (user picks a value from `items`) but renders a real text <input>,
 * so the selected value is shown as text that can be highlighted/copied.
 *
 * `items` are `{ label, value }` objects (same shape used by ERPSBaseTextField
 * select fields). `value` is the currently selected option's `value`, and
 * `onChange` is invoked with an event-like object `{ target: { value } }` so it
 * is a drop-in replacement for the select-style ERPSBaseTextField onChange.
 */
function ERPSBaseAutoComplete(props) {
  const {
    id,
    value,
    label,
    required,
    onChange,
    size,
    className,
    items = [],
    error,
    isDisabled,
    disabled,
    InputLabelProps,
    hasMargin,
    helperText,
    fullWidth = false,
    disableClearable = true,
  } = props;

  // Keep the full item list (including the leading blank option) so the user
  // can reset the field by selecting the blank first value instead of a clear
  // (X) button.
  const options = items;

  const selectedOption =
    items.find((option) => option?.value?.toString() === value?.toString()
    ) || null;

  const handleChange = (_event, newValue) => {
    if (onChange) {
      onChange({ target: { value: newValue ? newValue.value : "" } });
    }
  };

  // fullWidth=true: FormControl grows in flex layouts.
  // fullWidth=false: FormControl stays content-sized so FI CSS
  // (e.g. `.common__textField__width { width: 212px !important }`) controls width.
  const formControlSx = fullWidth
    ? { flex: "1 1 auto", maxWidth: "100%" }
    : { flex: "0 0 auto", maxWidth: "100%" };

  return (
    <FormControl sx={formControlSx} className={className}>
      {/* Copy-only label placed before the input so a full-page "select all +
          copy" emits "label value". The input supplies the value that follows;
          the floating label and notch legend are excluded from selection via
          the styled Autocomplete. aria-hidden avoids a duplicate screen-reader
          announcement (the input already exposes an aria-label). */}
      <VisuallyHiddenCopyText aria-hidden="true">
        {`${label} `}
      </VisuallyHiddenCopyText>
      <Autocomplete
        id={id}
        options={options}
        value={selectedOption}
        onChange={handleChange}
        size={size}
        disabled={isDisabled || disabled}
        fullWidth
        hasMargin={hasMargin}
        disableClearable={disableClearable}
        componentsProps={autocompleteDropdownComponentsProps}
        // Highlight the current value on open; arrow/hover then moves
        // .Mui-focused so the accent follows the active option. Avoid
        // autoHighlight, which would force the first matching option instead.
        selectOnFocus
        // Show the full list when the field still holds the selected label
        // (just opened); filter only after the user types a different query.
        filterOptions={(opts, state) => {
          const inputValue = state.inputValue ?? "";
          const selectedLabel = selectedOption?.label ?? "";
          if (!inputValue || inputValue === selectedLabel) {
            return opts;
          }
          const lower = inputValue.toLowerCase();
          return opts.filter((option) =>
            (option?.label ?? "").toLowerCase().includes(lower)
          );
        }}
        getOptionLabel={(option) => option?.label ?? ""}
        isOptionEqualToValue={(option, selected) =>
          option?.value?.toString() === selected?.value?.toString()
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size={size}
            label={label}
            error={error}
            required={required === "true"}
            helperText={helperText}
            InputLabelProps={{ shrink: true, ...InputLabelProps }}
            inputProps={{
              ...params.inputProps,
              "aria-label": `${label} ${selectedOption?.label ?? ""}`.trim(),
            }}
          />
        )}
      />
    </FormControl>
  );
}

ERPSBaseAutoComplete.propTypes = {
  label: PropTypes.string.isRequired,
  size: PropTypes.string,
  value: PropTypes.any,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any,
    })
  ),
  onChange: PropTypes.func,
};

export default withERPSTextFieldActionValidator(ERPSBaseAutoComplete);
